import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import { useModal } from '@/hooks';

import { useAppSelector } from '@/redux/hooks';
import { profileSelectors, userSelectors } from '@/redux/selectors';

import {
  Button,
  CopyText,
  FieldInfoGroup,
  SectionBox,
  SocialsContactModal,
} from '@/components';

import YourReferralLinksModal from '../YourReferralLinksModal/YourReferralLinksModal';
import { YourReferralLinksCreateModal } from '../YourReferralLinksCreateModal';
import { YourReferralLinksEditModal } from '../YourReferralLinksEditModal';

import styles from './ReferralProgram.module.sass';

interface ReferralProgramProps {}

const ReferralProgram: FC<ReferralProgramProps> = () => {
  const { t } = useTranslation();

  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const { data: profileData, loading: profileDataLoading } = useAppSelector(
    profileSelectors.getProfileSlice,
  );

  const [refLinkOnEdit, setRefLinkOnEdit] = useState(null);

  const {
    isOpen: isOpenYourReferralLinksModal,
    onOpenModal: handleOpenYourReferralLinksModal,
    onCloseModal: handleCloseYourReferralLinksModal,
  } = useModal();

  const {
    isOpen: isOpenYourReferralLinksEditModal,
    onOpenModal: handleOpenYourReferralLinksEditModal,
    onCloseModal: handleCloseYourReferralLinksEditModal,
  } = useModal();

  const {
    isOpen: isOpenYourReferralLinksAddModal,
    onOpenModal: handleOpenYourReferralLinksAddModal,
    onCloseModal: handleCloseYourReferralLinksAddModal,
  } = useModal();

  const {
    isOpen: isOpenSocialsContactModal,
    onOpenModal: handleOpenSocialsContactModal,
    onCloseModal: handleCloseSocialsContactModal,
  } = useModal();

  const openEditRefCode = (reflink: any) => {
    setRefLinkOnEdit(reflink);
    handleOpenYourReferralLinksEditModal();
  };

  const firstRenderActions = () => {
    if (!userData?.refProgram) {
      return (
        <Button
          className={styles.actionButton}
          type="secondary"
          corner="rect"
          onClick={handleOpenSocialsContactModal}
        >
          {t('translation.get_ref_link')}
        </Button>
      );
    }

    return null;
  };

  const secondRenderActions = () => {
    if (!!userData?.refProgram) {
      return (
        <Button
          className={styles.actionButton}
          type="secondary"
          corner="rect"
          onClick={handleOpenYourReferralLinksModal}
        >
          {t('translation.edit_reflinks_link')}
        </Button>
      );
    }

    return null;
  };

  const referralSectionTitle = t('translation.referral_program_title');
  const referralLinksTitle = t('translation.user_reflinks_label');

  return (
    <SectionBox>
      {{
        body: (
          <>
            <FieldInfoGroup
              title={referralSectionTitle}
              actions={firstRenderActions()}
              description={
                <div className={styles.referral__note}>
                  {parse(t('translation.user_reflink_advert_str'))}
                </div>
              }
            >
              {userData?.refProgram && profileData?.refData && (
                <div className={styles.referral__statistics}>
                  <h4 className={styles.referral__statistics_title}>
                    {t('translation.referral_program_statisctics')}
                  </h4>

                  <div className={styles.referral__statistics_info}>
                    <div className={styles.referral__statistics_info_item}>
                      <span
                        className={styles.referral__statistics_info_item_label}
                      >
                        {t('translation.your_kickback_fee')}:
                      </span>
                      <b>{profileData.refData.feePercent}%</b>
                    </div>
                    <div className={styles.referral__statistics_info_item}>
                      <span
                        className={styles.referral__statistics_info_item_label}
                      >
                        {t('translation.referals_number_label')}:
                      </span>
                      <b>{profileData.refData.count}</b>
                    </div>
                    <div className={styles.referral__statistics_info_item}>
                      <span
                        className={styles.referral__statistics_info_item_label}
                      >
                        {t('translation.commission_earned')}:
                      </span>
                      <b>{profileData.refData.bal} USDT</b>
                    </div>
                    <div className={styles.referral__statistics_info_item}>
                      <span
                        className={styles.referral__statistics_info_item_label}
                      >
                        {t('translation.commission_paid')}:
                      </span>
                      <b>{profileData.refData.wds} USDT</b>
                    </div>
                  </div>
                </div>
              )}
            </FieldInfoGroup>

            {userData?.refProgram && (
              <FieldInfoGroup
                title={
                  <div className={styles.referral__links_title}>
                    {referralLinksTitle}
                  </div>
                }
                actions={secondRenderActions()}
              >
                <div className={styles.referral__links}>
                  {profileData?.refLinks && profileData.refLinks.length > 0 ? (
                    <div className={styles.referral__list}>
                      {profileData.refLinks.map((link: string) => (
                        <div className={styles.referral__list_item} key={link}>
                          <CopyText text={link} copyText={link} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.referral__list}>
                      {t('translation.referral_program_empty_list')}
                    </div>
                  )}
                </div>
              </FieldInfoGroup>
            )}

            <YourReferralLinksModal
              isOpen={isOpenYourReferralLinksModal}
              onClose={handleCloseYourReferralLinksModal}
              onEdit={openEditRefCode}
              onAddNew={handleOpenYourReferralLinksAddModal}
            />

            <YourReferralLinksEditModal
              reflink={refLinkOnEdit}
              isOpen={isOpenYourReferralLinksEditModal}
              onClose={handleCloseYourReferralLinksEditModal}
            />

            <YourReferralLinksCreateModal
              isOpen={isOpenYourReferralLinksAddModal}
              onClose={handleCloseYourReferralLinksAddModal}
            />

            <SocialsContactModal
              title={t('translation.referral_link_msg')}
              onClose={handleCloseSocialsContactModal}
              isOpen={isOpenSocialsContactModal}
            />
          </>
        ),
      }}
    </SectionBox>
  );
};

export default ReferralProgram;
